import FormattedMessage from 'I18n/components/FormattedMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import _isEmpty from 'hs-lodash/isEmpty';
import _isNil from 'hs-lodash/isNil';
import _omitBy from 'hs-lodash/omitBy';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ACTIONS, SCREENS } from '../../constants/Tracking';
import { NON_HOST_EXPERIENCE_REFRESH_TIMEOUT } from '../../constants/timeouts';
import { objectTypes, toObjectTypes } from '../../constants/types';
import { AppContext } from '../../contexts/app/context';
import { ContactsPropsContext } from '../../contexts/contacts/PropsContext';
import { defaultContactsContextProps } from '../../contexts/contacts/constants';
import { ContactsContext, ContactsContextProvider } from '../../contexts/contacts/context';
import useAssociationModal from '../../hooks/useAssociationModal';
import useInterval from '../../hooks/useInterval';
import usePreviewModal from '../../hooks/usePreviewModal';
import FeaturePopupOver from '../common/FeaturePopupOver';
import PlaybookListInModal from '../common/playbooks/PlaybookListInModal';
import ContactsMainContent from './ContactsMainContent';
import MapExistingContactTab from './MapExistingContactTab/MapExistingContactTab';
import UnmapParticipantModal from './modals/UnmapParticipantModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function Contacts() {
  const {
    callObjectId,
    meetingUUID,
    usageTracker,
    canViewParticipants,
    isLoading,
    methods,
    hubspotUserId,
    portalId,
    openUrl,
    apiWrapper,
    createAttendeeContactCallback,
    updateCrmObjectCallback,
    hasLegacyDealAmountCalculation,
    featureTipsLink,
    prosemirrorTrackerProperties,
    reconnectRoute,
    intervalFetchMethod
  } = useContext(ContactsPropsContext);
  const {
    contactToAssociate,
    newContactEmail,
    participantToAssociate,
    contactsActions
  } = useContext(ContactsContext);
  const {
    isPastMeeting,
    selectors: {
      isDraft,
      isInLiveMeeting
    },
    appName,
    features
  } = useContext(AppContext);
  const [popupToUnmap, setPopupToUnmap] = useState({});
  const [selectedObjectId, setSelectedObjectId] = useState('');
  const onClickDissociate = ({
    participantId,
    contactId
  }) => {
    methods.dissociateParticipant({
      participantId,
      contactId
    });
  };
  const mapParticipant = () => {
    if (contactToAssociate.id) {
      methods.mapParticipant({
        participantId: participantToAssociate.participantId || '',
        contact: contactToAssociate
      });
    }
  };
  const createObjectHandler = arg => {
    methods.mapParticipant({
      participantId: participantToAssociate.participantId || '',
      contact: {
        firstName: arg.entity.properties.firstname || '',
        lastName: arg.entity.properties.lastName || '',
        email: arg.entity.properties.email || '',
        jobTitle: '',
        id: Number(arg.entity.id)
      }
    });
  };
  const getAssociations = useCallback(() => {
    if (callObjectId) {
      contactsActions.getAssociationSectionList({
        callObjectId,
        featureAvailable: features.ASSOCIATIONS_CONTACTS_WITH_CALL.available
      });
    }
  }, [callObjectId, contactsActions, features.ASSOCIATIONS_CONTACTS_WITH_CALL.available]);
  const associateAttendeeHandler = arg => {
    contactsActions.associateObject({
      objectId: +arg.entity.id,
      cb: getAssociations,
      callObjectId
    });
    createAttendeeContactCallback(arg.entity);
  };
  const [openAssociationModal, AssociationsModal] = useAssociationModal({
    hubspotUserId,
    portalId,
    crmObjectType: toObjectTypes.contact,
    openUrl,
    usageTracker,
    onSubmitAssociation: isDraft ? undefined : mapParticipant,
    isConfirmButtonEnabled: isDraft ? undefined : !_isEmpty(contactToAssociate),
    associateCrmObjectComponent: isDraft ? null : /*#__PURE__*/_jsx(MapExistingContactTab, {}),
    apiWrapper,
    newContactEmail,
    isContactsPage: true,
    lineItemsFeatureUnavailableComponent: features.CREATING_DEALS_WITH_LINE_ITEMS.available ? null : /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      marginBlockStart: "400",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "associationsPanel.tabs.create.associationProperties.featureDisabled"
      }), /*#__PURE__*/_jsx(FeaturePopupOver, {
        portalId: portalId,
        featureDisabledReason: features.CREATING_DEALS_WITH_LINE_ITEMS.reason,
        openUrl: openUrl,
        featureTipsLink: featureTipsLink,
        reconnectRoute: reconnectRoute
      })]
    }, "line-items-unavailable"),
    createObjectCallback: isDraft ? associateAttendeeHandler : createObjectHandler,
    callObjectId,
    hasLegacyDealAmountCalculation,
    prosemirrorTrackerProperties
  });
  const onClickAssociate = useCallback(contact => {
    usageTracker.trackInteraction(Object.assign({
      screen: SCREENS.participants
    }, ACTIONS.participants.clickMap));
    contactsActions.setParticpantToAssociate(contact);
    openAssociationModal();
  }, [usageTracker, openAssociationModal, contactsActions]);
  const [openPreviewModal, PreviewModal] = usePreviewModal({
    portalId,
    openUrl,
    usageTracker,
    crmObjectType: toObjectTypes.contact,
    extensionComponents: [/*#__PURE__*/_jsx(PlaybookListInModal, {
      callObjectId: Number(callObjectId),
      portalId: portalId,
      selectedObjectType: objectTypes.contacts,
      selectedObjectId: selectedObjectId,
      openUrl: openUrl,
      usageTracker: usageTracker,
      apiWrapper: apiWrapper.playbooks,
      featureTipsLink: featureTipsLink,
      reconnectRoute: reconnectRoute
    }, "playbooks")],
    apiWrapper,
    updateCrmObjectCallback,
    callObjectId,
    prosemirrorTrackerProperties,
    hubspotUserId
  });
  const openPreviewModalHandler = useCallback(contactId => {
    setSelectedObjectId(contactId);
    openPreviewModal(contactId);
  }, [openPreviewModal]);
  useInterval(intervalFetchMethod, NON_HOST_EXPERIENCE_REFRESH_TIMEOUT, {
    cancelOnUnmount: true
  });
  useEffect(() => {
    usageTracker.trackPageView(SCREENS.participants);
  }, [usageTracker]);
  useEffect(() => {
    if (isInLiveMeeting && callObjectId && canViewParticipants) {
      methods.fetchParticipants();
    } else if (meetingUUID && isPastMeeting && callObjectId) {
      methods.getParticipantsFromBackend();
    } else if (isDraft) {
      getAssociations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingUUID, callObjectId, canViewParticipants, isInLiveMeeting, isPastMeeting]);
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": `${appName}-participants-page`,
    children: [isLoading && (canViewParticipants === true || canViewParticipants === undefined) ? /*#__PURE__*/_jsx(LoadingOverlay, {
      contextual: true
    }) : null, /*#__PURE__*/_jsx(ContactsMainContent, {
      getAssociations: getAssociations,
      openPreviewModalHandler: openPreviewModalHandler,
      openAssociationModal: openAssociationModal,
      onClickAssociate: onClickAssociate,
      setPopupToUnmap: setPopupToUnmap
    }), AssociationsModal, PreviewModal, Object.keys(popupToUnmap).length ? /*#__PURE__*/_jsx(UnmapParticipantModal, {
      setPopupToUnmap: setPopupToUnmap,
      popupToUnmap: popupToUnmap,
      onRemoveSubmit: onClickDissociate,
      appName: appName
    }) : null]
  });
}
const WrappedAssociations = props => {
  const definedProps = _omitBy(props, _isNil);
  const apiWrapper = _omitBy(props.apiWrapper, _isNil);
  const properties = Object.assign({}, defaultContactsContextProps, definedProps, {
    apiWrapper: Object.assign({}, defaultContactsContextProps.apiWrapper, apiWrapper)
  });
  return /*#__PURE__*/_jsx(ContactsPropsContext.Provider, {
    value: properties,
    children: /*#__PURE__*/_jsx(ContactsContextProvider, {
      children: /*#__PURE__*/_jsx(Contacts, {})
    })
  });
};
export default WrappedAssociations;